/**
 * SettingRow — the standardized "label · description · scope · control" row.
 *
 * Extracted from the de-facto inline pattern the old god component repeated
 * ~15× (label + muted description on the left, control on the right, rows
 * separated by <Separator/>). Sections compose these inside a
 * <SettingsSection>; use `bullets` for the old `<ul class="list-disc">`
 * behaviour-notes pattern.
 */
import type { ReactNode } from 'react';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { ScopeBadge } from './ScopeBadge';
import type { SettingScope } from '../../areas';

/**
 * Ids the row owns and hands back to the control, so a Switch/Select can wire
 * `aria-describedby` without the caller re-deriving (and drifting from) the
 * id the row actually rendered.
 */
interface SettingRowControlIds {
  /** Id of the description <p>, or undefined when the row has no description. */
  describedBy: string | undefined;
}

interface SettingRowProps {
  /** Control element id, so the label focuses it. */
  htmlFor?: string;
  label: ReactNode;
  description?: ReactNode;
  /** Optional per-row scope badge when a row differs from its section. */
  scope?: SettingScope;
  /** Behaviour notes rendered as a small bulleted list under the description. */
  bullets?: ReactNode[];
  /**
   * The control (Switch / Select / Input / Button). Pass a function to receive
   * the row's generated ids — `{({ describedBy }) => <Switch aria-describedby={describedBy} …/>}`.
   */
  children: ReactNode | ((ids: SettingRowControlIds) => ReactNode);
  className?: string;
}

export function SettingRow({
  htmlFor,
  label,
  description,
  scope,
  bullets,
  children,
  className,
}: SettingRowProps) {
  // Only announce a description that actually exists, and only when there is a
  // control id to hang it off.
  const describedBy = htmlFor && description ? `${htmlFor}-desc` : undefined;
  return (
    <div
      className={cn(
        // Control stays INLINE with its label at every viewport — a toggle on
        // its own line reads as unanchored (Brett feedback 2026-07-19). Long
        // descriptions wrap in the left column instead.
        'flex flex-row items-center justify-between gap-3 py-3 first:pt-0 last:pb-0',
        className,
      )}
    >
      <div className="min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <Label htmlFor={htmlFor} className="text-sm font-medium">
            {label}
          </Label>
          {scope ? <ScopeBadge scope={scope} /> : null}
        </div>
        {description ? (
          <p id={describedBy} className="text-xs text-muted-foreground max-w-prose">
            {description}
          </p>
        ) : null}
        {bullets && bullets.length > 0 ? (
          <ul className="mt-1 list-inside list-disc space-y-0.5 text-xs text-muted-foreground/80">
            {bullets.map((b, i) => (
              <li key={i}>{b}</li>
            ))}
          </ul>
        ) : null}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {typeof children === 'function' ? children({ describedBy }) : children}
      </div>
    </div>
  );
}

/** Hairline divider between SettingRows (thin wrapper for consistency). */
export function SettingRowDivider() {
  return <div aria-hidden className="h-px w-full bg-border/70" />;
}
